// Peças visuais do módulo de Cronogramas. Linguagem própria: chrome em tinta
// escura flat, cor vinda dos status, ícones de traço (sem emoji/gradiente).
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';
import { ACCENT, avatarColor, dueState, faseColor, formatDate, initials, statusMeta } from './constants';

// Card de KPI da faixa de resumo: quadradinho de ícone com fundo suave + valor
// grande e rótulo. Sem sombra colorida (padrão do sistema).
export function StatCard({ icon, value, label, bg = '#EAF2FB', color = '#2B6CB0' }) {
 return (
  <Flex
   align='center'
   gap='14px'
   bg='#fff'
   border='1px solid #E6EAEE'
   borderRadius='14px'
   px='18px'
   py='16px'
  >
   <Flex
    align='center'
    justify='center'
    w='38px'
    h='38px'
    borderRadius='10px'
    bg={bg}
    color={color}
    flexShrink={0}
    fontSize='18px'
   >
    {icon}
   </Flex>
   <Box>
    <Text fontSize='20px' fontWeight='700' lineHeight='1' color='gray.800'>
     {value}
    </Text>
    <Text fontSize='11px' fontWeight='500' color='gray.500' letterSpacing='0.06em' textTransform='uppercase' mt='4px'>
     {label}
    </Text>
   </Box>
  </Flex>
 );
}

// Chip de filtro (pill). Ativo = tinta escura sólida; inativo = branco com borda.
export function FilterChip({ label, active, onClick }) {
 return (
  <Box
   as='button'
   type='button'
   onClick={onClick}
   bg={active ? '#1C2733' : '#fff'}
   color={active ? '#fff' : '#4A5560'}
   border={`1px solid ${active ? '#1C2733' : '#E0E5EA'}`}
   borderRadius='999px'
   px='14px'
   py='7px'
   fontSize='13px'
   fontWeight='500'
   whiteSpace='nowrap'
   transition='all .12s ease'
   _hover={active ? undefined : { bg: '#F4F6F8' }}
  >
   {label}
  </Box>
 );
}

// Chip de status sólido com a cor do status.
export function StatusPill({ status, size = 'sm' }) {
 const m = statusMeta(status);
 return (
  <Box
   as='span'
   display='inline-flex'
   alignItems='center'
   bg={m.solid}
   color={m.on}
   borderRadius='6px'
   px={size === 'lg' ? '12px' : '8px'}
   py={size === 'lg' ? '4px' : '2px'}
   fontSize={size === 'lg' ? '12px' : '10.5px'}
   fontWeight='800'
   letterSpacing='0.06em'
   textTransform='uppercase'
   whiteSpace='nowrap'
  >
   {m.label}
  </Box>
 );
}

// Avatar circular com cor sólida e iniciais.
export function Avatar({ name, size = 30, ring = '#fff' }) {
 return (
  <Tooltip label={name} hasArrow openDelay={300}>
   <Flex
    align='center'
    justify='center'
    w={`${size}px`}
    h={`${size}px`}
    borderRadius='999px'
    bg={avatarColor(name || '')}
    color='#fff'
    fontSize={`${Math.round(size * 0.38)}px`}
    fontWeight='700'
    border={`2px solid ${ring}`}
    flexShrink={0}
   >
    {initials(name)}
   </Flex>
  </Tooltip>
 );
}

// Pilha sobreposta de avatares.
export function AvatarStack({ people = [], size = 30, max = 4 }) {
 const shown = people.slice(0, max);
 const rest = people.length - shown.length;
 if (people.length === 0) {
  return (
   <Text fontSize='sm' color='gray.400'>
    —
   </Text>
  );
 }
 return (
  <Flex align='center'>
   {shown.map((p, i) => (
    <Box key={p.id_user || i} ml={i === 0 ? '0' : '-8px'} zIndex={shown.length - i}>
     <Avatar name={p.username || p.name} size={size} />
    </Box>
   ))}
   {rest > 0 && (
    <Flex
     align='center'
     justify='center'
     ml='-8px'
     w={`${size}px`}
     h={`${size}px`}
     borderRadius='999px'
     bg='gray.200'
     color='gray.600'
     fontSize='11px'
     fontWeight='700'
     border='2px solid #fff'
    >
     +{rest}
    </Flex>
   )}
  </Flex>
 );
}

// Barra de progresso plana (verde funcional).
export function ProgressBar({ pct = 0, height = 8, track = 'rgba(0,0,0,0.12)' }) {
 return (
  <Box w='100%' h={`${height}px`} bg={track} borderRadius='2px' overflow='hidden'>
   <Box
    h='100%'
    w={`${Math.max(pct, pct > 0 ? 4 : 0)}%`}
    bg='#00C875'
    transition='width .35s ease'
   />
  </Box>
 );
}

// Chip de prazo: mostra a data de fim; fundo neutro, âmbar (vence hoje) ou
// vermelho (atrasada). Tooltip explica o estado.
export function DueChip({ endDate, status }) {
 const state = dueState(endDate, status);
 const cfg =
  state === 'overdue'
   ? { bg: '#FDECEC', fg: '#C0392B', hint: 'Atrasada' }
   : state === 'today'
   ? { bg: '#FFF4E1', fg: '#9A6400', hint: 'Vence hoje' }
   : { bg: '#F2F4F7', fg: '#6B7682', hint: '' };
 const chip = (
  <Flex
   as='span'
   align='center'
   gap='5px'
   bg={cfg.bg}
   color={cfg.fg}
   fontSize='11px'
   fontWeight={state ? '700' : '500'}
   borderRadius='6px'
   px='8px'
   py='3px'
   whiteSpace='nowrap'
  >
   <FiCalendar size={12} />
   {formatDate(endDate)}
  </Flex>
 );
 return cfg.hint ? <Tooltip label={cfg.hint} hasArrow>{chip}</Tooltip> : chip;
}

// Barra de progresso segmentada: concluído (verde) / em andamento (laranja) /
// impedida (vermelho), proporcional às atividades vivas, com legenda.
export function SegmentedProgress({ done = 0, doing = 0, blocked = 0, total = 0 }) {
 const pct = (n) => (total ? (n / total) * 100 : 0);
 const legend = [
  { color: '#00C875', label: 'Concluído' },
  { color: '#FDAB3D', label: 'Em andamento' },
  { color: '#E2445C', label: 'Impedida' },
 ];
 return (
  <Box>
   <Flex h='8px' borderRadius='999px' overflow='hidden' bg='#EEF1F4'>
    <Box w={`${pct(done)}%`} bg='#00C875' transition='width .35s ease' />
    <Box w={`${pct(doing)}%`} bg='#FDAB3D' transition='width .35s ease' />
    <Box w={`${pct(blocked)}%`} bg='#E2445C' transition='width .35s ease' />
   </Flex>
   <Flex gap='14px' mt='6px' flexWrap='wrap'>
    {legend.map((l) => (
     <Flex key={l.label} align='center' gap='5px' fontSize='11px' color='gray.500'>
      <Box w='7px' h='7px' borderRadius='2px' bg={l.color} />
      {l.label}
     </Flex>
    ))}
   </Flex>
  </Box>
 );
}

// Tag colorida da Fase/etapa da atividade (determinística por texto).
export function FaseTag({ fase }) {
 if (!fase) return null;
 const c = faseColor(fase);
 return (
  <Box
   as='span'
   display='inline-block'
   bg={c.bg}
   color={c.color}
   fontSize='10px'
   fontWeight='800'
   letterSpacing='0.04em'
   textTransform='uppercase'
   borderRadius='6px'
   px='7px'
   py='2px'
   maxW='100%'
   overflow='hidden'
   textOverflow='ellipsis'
   whiteSpace='nowrap'
  >
   {fase}
  </Box>
 );
}

// Cabeçalho de identidade do módulo — superfície branca destacada do fundo,
// com uma faixa de acento no topo (azul por padrão; cor do status no detalhe).
export function ModuleBanner({ accent = ACCENT, children, ...rest }) {
 return (
  <Box
   borderRadius='14px'
   p={{ base: '20px', md: '24px' }}
   bg='#fff'
   border='1px solid #E6E8EC'
   borderTop={`3px solid ${accent}`}
   boxShadow='0 6px 18px rgba(17,24,39,0.05)'
   {...rest}
  >
   {children}
  </Box>
 );
}
